import type { Surface } from '@funkit/connect-core'
import { useEffect, useRef } from 'react'

import { FiatStatusScreen } from '../FiatStatusScreen'
import { logger } from '../../logging/logger'
import { useFunkitTranslation } from '../../providers/FunkitI18nProvider'
import type { SurfaceAdapter, SurfaceContext } from './adapter'
import { describeThrown } from './describeThrown'
import type { StripeOnrampClient } from './stripeOnramp'
import {
  stripeAuthSurface,
  stripeAuthorizationReport,
  stripeSeamlessAuthReport,
} from './stripeSessionAuth'

/** Stripe owns OTP/consent; this adapter owns only launch and report. */
export function stripeAuthorizeAdapter(
  stripe: StripeOnrampClient,
  attempt: object,
): SurfaceAdapter {
  return (surface: Surface) => {
    const auth = stripeAuthSurface(surface)
    if (auth === undefined) {
      return undefined
    }
    return {
      control: 'HANDOFF',
      render: (context) => (
        <StripeAuthorizeSurface
          stripe={stripe}
          auth={auth}
          attempt={attempt}
          context={context}
        />
      ),
    }
  }
}

function StripeAuthorizeSurface({
  stripe,
  auth,
  attempt,
  context,
}: {
  stripe: StripeOnrampClient
  auth: NonNullable<ReturnType<typeof stripeAuthSurface>>
  attempt: object
  context: SurfaceContext
}) {
  const { t } = useFunkitTranslation()
  const mounted = useRef(true)
  // The surface parser returns a fresh object when its parent renders, so the
  // parsed auth object is not an attempt identity. The step response is: keep
  // one presentation per response object, while allowing a newly returned
  // SESSION_AUTH response (including an intentional cancellation retry) to
  // present again.
  const lastStartedAttempt = useRef<object | undefined>(undefined)
  const contextRef = useRef(context)
  contextRef.current = context

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (lastStartedAttempt.current === attempt) {
      return
    }
    lastStartedAttempt.current = attempt
    if (auth.action === 'AUTHENTICATE_WITH_TOKEN') {
      void stripe
        .authenticateUserWithToken(auth.linkAuthTokenClientSecret)
        .then((result) => {
          if (!mounted.current) {
            return
          }
          const authenticated = result.error === undefined
          logger.info('connect-rn:fiat:stripe_seamless_auth_returned', {
            authenticated,
            ...(result.error ? { code: result.error.code } : {}),
          })
          contextRef.current.report(stripeSeamlessAuthReport(authenticated))
        })
        .catch((error: unknown) => {
          if (!mounted.current) {
            return
          }
          logger.warn('connect-rn:fiat:stripe_seamless_auth_failed', {
            error: describeThrown(error),
          })
          contextRef.current.report(stripeSeamlessAuthReport(false))
        })
      return
    }
    void stripe
      .authorize(auth.linkAuthIntentId)
      .then((result) => {
        if (!mounted.current) {
          return
        }
        const report = stripeAuthorizationReport(result)
        if (report !== undefined) {
          logger.info('connect-rn:fiat:stripe_authorization_returned', {
            status: result.status ?? 'Cancelled',
          })
          contextRef.current.report(report)
          return
        }
        logger.warn('connect-rn:fiat:stripe_authorization_failed', {
          code: result.error?.code ?? 'unknown',
        })
        contextRef.current.onFailed(result.error?.code ?? 'unknown')
      })
      .catch((error: unknown) => {
        if (!mounted.current) {
          return
        }
        // `String(error)` gives "[object Object]" here: the SDK rejects with a plain
        // `{ code, message }`, not an Error, so the one field we logged was the one
        // that never said why. Same trap as StripeSessionAuthController.
        logger.warn('connect-rn:fiat:stripe_authorization_failed', {
          error: describeThrown(error),
        })
        contextRef.current.onFailed('unknown')
      })
  }, [attempt, auth, stripe])

  return (
    <FiatStatusScreen
      tone="waiting"
      title={t('fiat.stripeAuthorizing')}
      detail={t('fiat.stripeAuthorizingDetail')}
    />
  )
}
